import type { GatewayAccountView, PaymentSettingsView } from "@kopi/contracts";
import type { PaymentProvider } from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@kopi/ui/components/card";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Switch } from "@kopi/ui/components/switch";
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CopyIcon, PencilIcon, PlusIcon, QrCodeIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { Page, PageHeader } from "@/components/page-header";
import { paymentSettings } from "@/i18n/messages/payment-settings";
import { useMessages } from "@/i18n/provider";
import { ApiRequestError, api, unwrap } from "@/lib/api";

const KEY = ["payment-settings"] as const;
const settingsQuery = queryOptions({
  queryKey: KEY,
  queryFn: async (): Promise<PaymentSettingsView> => unwrap(await api.v1["payment-settings"].get()),
});

export const Route = createFileRoute("/_authed/settings/payments")({
  staticData: {
    section: "preferences",
    crumb: "page.payments",
    parent: { label: "nav.settings", to: "/settings" },
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(settingsQuery),
  component: PaymentSettingsPage,
});

const useT = () => useMessages(paymentSettings);
type T = ReturnType<typeof useT>["t"];
type Entity = PaymentSettingsView["legalEntities"][number];

const HEALTH_VARIANT = {
  healthy: "success",
  degraded: "warning",
  unhealthy: "destructive",
  unknown: "outline",
} as const;

/** The API's refusal, in the interface language (its English message is never shown). */
function failure(t: T, error: unknown): string {
  const code = error instanceof ApiRequestError ? error.code : null;
  if (code === "account_revision_conflict" || code === "method_revision_conflict")
    return t("paymentSettings.error.changed");
  if (code === "credentials_unavailable") return t("paymentSettings.error.credentials");
  if (code === "provider_unavailable") return t("paymentSettings.error.provider");
  if (code === "merchant_required") return t("paymentSettings.error.merchantRequired");
  return t("paymentSettings.error.failed");
}

/**
 * Payment settings (R1-27, PRD PAY-01): QRIS on or off per legal entity, with per-outlet overrides;
 * the legal entity's gateway accounts with their health and webhook URL. Credentials go in, sealed on
 * the server, and never come back (AC-33).
 */
function PaymentSettingsPage() {
  const { t } = useT();
  const { data } = useSuspenseQuery(settingsQuery);
  return (
    <Page width="narrow">
      <PageHeader title={t("page.payments")} subtitle={t("paymentSettings.subtitle")} />
      {data.legalEntities.length === 0 && (
        <p className="text-muted-foreground">{t("paymentSettings.none")}</p>
      )}
      {data.legalEntities.map((entity) => (
        <EntityCard key={entity.id} entity={entity} providers={data.providers} />
      ))}
    </Page>
  );
}

function useSave() {
  const queryClient = useQueryClient();
  const { t } = useT();
  return {
    done: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      await queryClient.invalidateQueries({ queryKey: ["payment-methods"] });
      toast.success(t("paymentSettings.saved"));
    },
  };
}

function EntityCard(props: { entity: Entity; providers: PaymentSettingsView["providers"] }) {
  const { entity } = props;
  const { t } = useT();
  const { done } = useSave();
  const [editing, setEditing] = useState<GatewayAccountView | "new" | null>(null);
  // No provider the server can create accounts for: the dialog would offer an empty combobox.
  const noProviders = !props.providers.sumopod && !props.providers.fake;
  const qrisOn = entity.setting?.enabled ?? true;
  const toggle = useMutation({
    mutationFn: async (enabled: boolean) =>
      unwrap(
        await api.v1["payment-methods"].qris.put({
          legalEntityId: entity.id,
          outletId: null,
          enabled,
          expectedRevision: entity.setting?.revision ?? 0,
        }),
      ),
    onSuccess: done,
    onError: (error) => toast.error(failure(t, error)),
  });
  const switchId = useId();
  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{entity.name}</CardTitle>
        <CardDescription>{t("paymentSettings.qrisHint")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="flex min-h-11 items-center justify-between gap-3">
          <Label htmlFor={switchId} className="font-medium">
            {t("paymentSettings.qrisOn")}
          </Label>
          <Switch
            id={switchId}
            checked={qrisOn}
            disabled={!entity.manageable || toggle.isPending}
            onCheckedChange={(checked) => toggle.mutate(checked)}
          />
        </div>

        <section className="flex flex-col gap-3" aria-labelledby={`${switchId}-accounts`}>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 id={`${switchId}-accounts`} className="font-semibold">
                {t("paymentSettings.accounts")}
              </h2>
              <p className="text-muted-foreground text-sm">{t("paymentSettings.accountsHint")}</p>
            </div>
            {entity.manageable && (
              <Button
                type="button"
                variant="outline"
                disabled={noProviders}
                onClick={() => setEditing("new")}
              >
                <PlusIcon aria-hidden />
                {t("paymentSettings.addAccount")}
              </Button>
            )}
          </div>
          {entity.manageable && noProviders && (
            <p className="text-muted-foreground text-sm">{t("paymentSettings.noProviders")}</p>
          )}
          {entity.accounts.length === 0 && (
            <p className="text-muted-foreground text-sm">{t("paymentSettings.noAccounts")}</p>
          )}
          {/* Not paginated (CODING_STANDARDS §4 exception): a legal entity has a handful of accounts. */}
          <ul className="flex flex-col gap-3">
            {entity.accounts.map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                manageable={entity.manageable}
                onEdit={() => setEditing(account)}
              />
            ))}
          </ul>
        </section>

        <OutletOverrides entity={entity} method="qris" />

        <StaticQrisSection entity={entity} />
      </CardContent>
      {editing && (
        <AccountDialog
          entity={entity}
          account={editing === "new" ? null : editing}
          providers={props.providers}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

function AccountRow(props: { account: GatewayAccountView; manageable: boolean; onEdit: () => void }) {
  const { account } = props;
  const { t } = useT();
  const health = account.health;
  return (
    <li className="flex flex-col gap-2 rounded-xl border p-4" data-testid="gateway-account">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{account.label}</span>
        <Badge variant={account.status === "active" ? "success" : "outline"}>
          {t(account.status === "active" ? "paymentSettings.active" : "paymentSettings.disabled")}
        </Badge>
        <Badge variant={HEALTH_VARIANT[health.status]} data-testid="gateway-health">
          {t(`paymentSettings.health.${health.status}`)}
        </Badge>
        {props.manageable && (
          <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={props.onEdit}>
            <PencilIcon aria-hidden />
            {t("paymentSettings.editAccount")}
          </Button>
        )}
      </div>
      <p className="text-muted-foreground text-sm">
        {t(`paymentSettings.provider.${account.provider}`)} · {account.merchantId} ·{" "}
        {t(account.hasCredentials ? "paymentSettings.hasCredentials" : "paymentSettings.noCredentials")}
      </p>
      {health.calls > 0 && (
        <p className="text-muted-foreground text-sm">
          {t("paymentSettings.healthDetail", {
            calls: health.calls,
            errors: Math.round((health.errorRate ?? 0) * health.calls),
          })}
          {health.webhookLagSeconds !== null &&
            ` · ${t("paymentSettings.lagDetail", { seconds: Math.round(health.webhookLagSeconds) })}`}
        </p>
      )}
      <div className="flex flex-col gap-1">
        <span className="font-medium text-sm">{t("paymentSettings.webhookUrl")}</span>
        <div className="flex items-center gap-2">
          <code
            className="min-w-0 flex-1 truncate rounded-md bg-muted px-2 py-1 text-xs"
            title={account.webhookUrl}
          >
            {account.webhookUrl}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              void navigator.clipboard
                .writeText(account.webhookUrl)
                .then(() => toast.success(t("paymentSettings.copied")))
            }
          >
            <CopyIcon aria-hidden />
            {t("paymentSettings.copy")}
          </Button>
        </div>
        <span className="text-muted-foreground text-xs">{t("paymentSettings.webhookUrlHint")}</span>
      </div>
    </li>
  );
}

const FOLLOW = "follow";

/** Per-outlet overrides of one method: gateway QRIS, or the static-QRIS fallback (R1-28). */
function OutletOverrides(props: { entity: Entity; method: "qris" | "manual"; disabled?: boolean }) {
  const { entity, method } = props;
  const { t } = useT();
  const { done } = useSave();
  const overrides = method === "qris" ? entity.overrides : entity.manualQris.overrides;
  const save = useMutation({
    mutationFn: async (input: { outletId: string; enabled: boolean | null }) => {
      const body = {
        legalEntityId: entity.id,
        outletId: input.outletId,
        enabled: input.enabled,
        expectedRevision: overrides.find((o) => o.outletId === input.outletId)?.revision ?? 0,
      };
      return unwrap(
        method === "qris"
          ? await api.v1["payment-methods"].qris.put(body)
          : await api.v1["payment-methods"]["manual-qris-static"].put(body),
      );
    },
    onSuccess: done,
    onError: (error) => toast.error(failure(t, error)),
  });
  const label = t(method === "qris" ? "paymentSettings.qris" : "paymentSettings.staticQris");
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="font-semibold">{t("paymentSettings.outlets")}</h2>
        <p className="text-muted-foreground text-sm">
          {t(method === "qris" ? "paymentSettings.outletsHint" : "paymentSettings.staticOutletsHint")}
        </p>
      </div>
      {/* Not paginated (CODING_STANDARDS §4 exception): a legal entity's outlets are a small set. */}
      <ul className="flex flex-col divide-y">
        {entity.outlets.map((outlet) => {
          const override = overrides.find((o) => o.outletId === outlet.id);
          const value = override?.enabled === true ? "on" : override?.enabled === false ? "off" : FOLLOW;
          return (
            <li key={outlet.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{outlet.name}</span>
                <span className="text-muted-foreground text-sm">{outlet.code}</span>
              </span>
              <OptionSelect
                aria-label={`${label} · ${outlet.name}`}
                className="md:w-56"
                disabled={!outlet.editable || save.isPending || props.disabled === true}
                options={[
                  { value: FOLLOW, label: t("paymentSettings.follow") },
                  { value: "on", label: t("paymentSettings.on") },
                  { value: "off", label: t("paymentSettings.off") },
                ]}
                value={value}
                onValueChange={(next) =>
                  save.mutate({ outletId: outlet.id, enabled: next === FOLLOW ? null : next === "on" })
                }
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * The static-QRIS fallback (R1-28, PRD PAY-03): only for a gateway outage, and only with the merchant's
 * separately registered static QRIS. Off unless enabled — for the legal entity by the owner, or at an
 * outlet by its manager. Payments taken with it are BELUM LUNAS until verified against the merchant
 * account (Verifikasi QRIS statis).
 */
function StaticQrisSection(props: { entity: Entity }) {
  const { entity } = props;
  const { t } = useT();
  const { done } = useSave();
  const [editing, setEditing] = useState(false);
  const setting = entity.manualQris.setting;
  const merchant = setting?.merchant ?? null;
  const switchId = useId();
  const toggle = useMutation({
    mutationFn: async (enabled: boolean) =>
      unwrap(
        await api.v1["payment-methods"]["manual-qris-static"].put({
          legalEntityId: entity.id,
          outletId: null,
          enabled,
          expectedRevision: setting?.revision ?? 0,
        }),
      ),
    onSuccess: done,
    onError: (error) => toast.error(failure(t, error)),
  });
  return (
    <section className="flex flex-col gap-4 border-t pt-6" aria-labelledby={`${switchId}-static`}>
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
        <div>
          <h2 id={`${switchId}-static`} className="flex items-center gap-2 font-semibold">
            <QrCodeIcon className="size-4" aria-hidden />
            {t("paymentSettings.staticQris")}
          </h2>
          <p className="text-muted-foreground text-sm">{t("paymentSettings.staticHint")}</p>
        </div>
        {entity.manageable && (
          <Button type="button" variant="outline" onClick={() => setEditing(true)}>
            <PencilIcon aria-hidden />
            {t(merchant ? "paymentSettings.staticEdit" : "paymentSettings.staticRegister")}
          </Button>
        )}
      </div>
      <p className="text-sm" data-testid="static-qris-merchant">
        {merchant
          ? merchant.nmid
            ? t("paymentSettings.staticMerchantNmid", { name: merchant.name, nmid: merchant.nmid })
            : t("paymentSettings.staticMerchant", { name: merchant.name })
          : t("paymentSettings.staticNoMerchant")}
      </p>
      <div className="flex min-h-11 items-center justify-between gap-3">
        <Label htmlFor={switchId} className="font-medium">
          {t("paymentSettings.staticOn")}
        </Label>
        <Switch
          id={switchId}
          checked={setting?.enabled === true}
          disabled={!entity.manageable || !merchant || toggle.isPending}
          onCheckedChange={(checked) => toggle.mutate(checked)}
        />
      </div>
      <OutletOverrides entity={entity} method="manual" disabled={!merchant} />
      {editing && <StaticMerchantDialog entity={entity} onClose={() => setEditing(false)} />}
    </section>
  );
}

/** Registers (or corrects) the legal entity's static QRIS: the merchant name printed on it and its NMID. */
function StaticMerchantDialog(props: { entity: Entity; onClose: () => void }) {
  const { entity } = props;
  const { t } = useT();
  const { done } = useSave();
  const ids = { name: useId(), nmid: useId() };
  const setting = entity.manualQris.setting;
  const [name, setName] = useState(setting?.merchant?.name ?? "");
  const [nmid, setNmid] = useState(setting?.merchant?.nmid ?? "");
  const cleanNmid = nmid.trim().toUpperCase();
  const valid = name.trim().length >= 2 && (cleanNmid === "" || /^ID\d{10,15}$/.test(cleanNmid));
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1["payment-methods"]["manual-qris-static"].put({
          legalEntityId: entity.id,
          outletId: null,
          enabled: setting?.enabled ?? false,
          merchant: { name: name.trim(), nmid: cleanNmid === "" ? null : cleanNmid },
          expectedRevision: setting?.revision ?? 0,
        }),
      ),
    onSuccess: async () => {
      await done();
      props.onClose();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("paymentSettings.staticDialog")}
      description={entity.name}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="static-qris-merchant" disabled={!valid || save.isPending}>
          {t(save.isPending ? "paymentSettings.saving" : "paymentSettings.save")}
        </Button>
      }
    >
      <form
        id="static-qris-merchant"
        className="flex flex-col gap-4"
        onSubmit={submit}
        noValidate
        autoComplete="off"
      >
        <p className="text-muted-foreground text-sm">{t("paymentSettings.staticDialogHint")}</p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.name}>{t("paymentSettings.staticName")}</Label>
          <Input id={ids.name} maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.nmid}>{t("paymentSettings.staticNmid")}</Label>
          <Input
            id={ids.nmid}
            maxLength={17}
            spellCheck={false}
            className="font-mono uppercase"
            value={nmid}
            onChange={(e) => setNmid(e.target.value)}
          />
          <p className="text-muted-foreground text-sm">{t("paymentSettings.staticNmidHint")}</p>
        </div>
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {failure(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}

/** Adds an account, or edits one: label, merchant, credential rotation, and which account is active. */
function AccountDialog(props: {
  entity: Entity;
  account: GatewayAccountView | null;
  providers: PaymentSettingsView["providers"];
  onClose: () => void;
}) {
  const { account } = props;
  const { t } = useT();
  const { done } = useSave();
  const ids = { provider: useId(), label: useId(), merchant: useId(), key: useId(), secret: useId() };
  const providers = (["sumopod", "fake"] as const).filter((p) => props.providers[p]);
  // A dead dialog is never offered (the add button is disabled), but the form itself must not be
  // submittable either: the server would refuse `provider_unavailable` after the fact.
  const providerAvailable = account !== null || providers.length > 0;
  const [provider, setProvider] = useState<PaymentProvider>(account?.provider ?? providers[0] ?? "sumopod");
  const [label, setLabel] = useState(account?.label ?? "");
  const [merchantId, setMerchantId] = useState(account?.merchantId ?? "");
  const [apiKey, setApiKey] = useState("");
  const [secret, setSecret] = useState("");
  const [status, setStatus] = useState(account?.status ?? "active");
  const needsCredentials = provider === "sumopod";
  const rotating = apiKey !== "" || secret !== "";
  const credentialsValid = apiKey.trim().length >= 8 && /^whsec_[A-Za-z0-9+/=]{16,}$/.test(secret.trim());
  const valid =
    providerAvailable &&
    label.trim() !== "" &&
    merchantId.trim() !== "" &&
    (account ? !rotating || credentialsValid : !needsCredentials || credentialsValid);
  const save = useMutation({
    mutationFn: async () => {
      const credentials =
        rotating || (!account && needsCredentials)
          ? { credentials: { apiKey: apiKey.trim(), webhookSecret: secret.trim() } }
          : {};
      if (!account) {
        return unwrap(
          await api.v1["payment-accounts"].post({
            legalEntityId: props.entity.id,
            provider,
            label: label.trim(),
            merchantId: merchantId.trim(),
            ...credentials,
          }),
        );
      }
      return unwrap(
        await api.v1["payment-accounts"]({ accountId: account.id }).patch({
          expectedRevision: account.revision,
          label: label.trim(),
          merchantId: merchantId.trim(),
          ...(status !== account.status ? { status } : {}),
          ...credentials,
        }),
      );
    },
    onSuccess: async () => {
      await done();
      props.onClose();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && !save.isPending) save.mutate();
  }
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t(account ? "paymentSettings.editAccount" : "paymentSettings.addAccount")}
      description={props.entity.name}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="gateway-account" disabled={!valid || save.isPending}>
          {t(save.isPending ? "paymentSettings.saving" : "paymentSettings.save")}
        </Button>
      }
    >
      <form
        id="gateway-account"
        className="flex flex-col gap-4"
        onSubmit={submit}
        noValidate
        autoComplete="off"
      >
        {!account && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.provider}>{t("paymentSettings.provider")}</Label>
            <OptionSelect
              id={ids.provider}
              options={providers.map((p) => ({ value: p, label: t(`paymentSettings.provider.${p}`) }))}
              value={provider}
              onValueChange={(v) => setProvider(v as PaymentProvider)}
            />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.label}>{t("paymentSettings.label")}</Label>
          <Input
            id={ids.label}
            maxLength={80}
            value={label}
            placeholder={t("paymentSettings.labelPlaceholder")}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.merchant}>{t("paymentSettings.merchantId")}</Label>
          <Input
            id={ids.merchant}
            maxLength={128}
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
          />
          <p className="text-muted-foreground text-sm">{t("paymentSettings.merchantHint")}</p>
        </div>
        {needsCredentials && (
          <fieldset className="flex flex-col gap-4">
            {account && <legend className="mb-1 font-medium text-sm">{t("paymentSettings.rotate")}</legend>}
            {account && <p className="text-muted-foreground text-sm">{t("paymentSettings.rotateHint")}</p>}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ids.key}>{t("paymentSettings.apiKey")}</Label>
              <Input
                id={ids.key}
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
              <p className="text-muted-foreground text-sm">{t("paymentSettings.apiKeyHint")}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ids.secret}>{t("paymentSettings.secret")}</Label>
              <Input
                id={ids.secret}
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
              />
              <p className="text-muted-foreground text-sm">{t("paymentSettings.secretHint")}</p>
            </div>
          </fieldset>
        )}
        {account && (
          <div className="flex min-h-11 items-center justify-between gap-3">
            <Label htmlFor={`${ids.label}-active`}>{t("paymentSettings.activate")}</Label>
            <Switch
              id={`${ids.label}-active`}
              checked={status === "active"}
              onCheckedChange={(checked) => setStatus(checked ? "active" : "disabled")}
            />
          </div>
        )}
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {failure(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}
